import { FC, useState } from 'react';

import { formatMoney } from '@betterment/core';
import {
  Box,
  Button,
  Dialog,
  DropdownMenu,
  IconButton,
  Sheet,
  Table,
  Tag,
  Text,
  VisuallyHidden,
} from '@betterment/samba-react';
import { nanoid } from 'nanoid';
import { Link } from 'react-router-dom';

import BillingPlanTableBodyRowCell from './BillingPlanTableBodyRowCell';
import { BillingPlanPageDrawer } from './components/drawer/BillingPlanPageDrawer';
import { NumberOfClients } from './components/NumberOfClients';

import { BillingPlan, PlatformFeePaidBy } from '../../../../graphql/schema';
import urlBuilder from '../../../../Shared/url-builder';
import { BillingRateType, PaymentTypeMap, TIERED_CALCULATION_LABEL, isTieredBillingRate } from '../data';
import SetDefaultModal from '../SetDefaultModal';
import ViewTieredRateModal from '../view-tiered-rate-modal/ViewTieredRateModal';

const PlatformFeeText: FC<{ plan: BillingPlan; platformFee: number }> = ({ plan, platformFee }) => {
  const { platformFeeAllocation } = plan;
  if (platformFeeAllocation.__typename === 'SharedPlatformFeeAllocation') {
    const { clientPortion } = platformFeeAllocation;
    return (
      <Box css={{ display: 'flex', flexDirection: 'column' }}>
        <Text>Shared</Text>
        <Text color="text.weak">
          Client {clientPortion} bps, your firm {Math.max(platformFee - clientPortion, 0)} bps
        </Text>
      </Box>
    );
  }

  switch (platformFeeAllocation.paidBy) {
    case PlatformFeePaidBy.ClientOnly:
      return <Text>Client</Text>;
    case PlatformFeePaidBy.AdvisorOnly:
      return <Text>Your firm</Text>;
    default:
      return <Text>N/A</Text>;
  }
};

const BillingPlanIndexPageRow: FC<{
  plan: BillingPlan;
  isLastIndex: boolean;
  defaultBillingPlan: BillingPlan;
  platformFee: number;
}> = ({ plan, isLastIndex, defaultBillingPlan, platformFee }) => {
  const [isDefaultModalIsOpen, setDefaultModalIsOpen] = useState(false);
  const [isTieredBillingModalOpen, setIsTieredBillingModalOpen] = useState<boolean>(false);

  const getRate = (billingPlan: BillingPlan) => {
    const { billingRate } = billingPlan;
    switch (billingRate.__typename) {
      case BillingRateType.AssetBased:
        return `${billingRate.feeBps} bps`;
      case BillingRateType.Fixed:
        return formatMoney(billingRate.feeCents);
      case BillingRateType.Tiered:
        return (
          <Dialog open={isTieredBillingModalOpen} onOpenChange={setIsTieredBillingModalOpen} overlay="default">
            <Dialog.Trigger asChild={true}>
              <Button variant="textPrimary">Tiered</Button>
            </Dialog.Trigger>
            <ViewTieredRateModal
              plan={plan}
              platformFee={platformFee}
              onClose={() => setIsTieredBillingModalOpen(false)}
            />
          </Dialog>
        );
      default:
        return 'N/A';
    }
  };

  return (
    <Table.Row aria-label="billingPlanRow">
      <Sheet overlay="weak">
        <BillingPlanTableBodyRowCell planName={plan.name} isLastIndex={isLastIndex}>
          <Box css={{ display: 'inline-flex', alignItems: 'center', maxWidth: '40rem' }}>
            <Text
              aria-label="billingPlanName"
              css={{
                display: 'inline-block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                paddingRight: 'space.50',
              }}
            >
              {plan.name}
            </Text>
            {plan.firmDefault && <Tag accent="blue">Firm default</Tag>}
          </Box>
          <Text as="p" variant="text50" color="text.weak">
            {plan.latestChangeDescription}
          </Text>
        </BillingPlanTableBodyRowCell>
        <BillingPlanTableBodyRowCell isLastIndex={isLastIndex}>
          <Box css={{ display: 'flex', flexDirection: 'column', maxWidth: '14rem' }}>
            <Text aria-label="billingPlanRate">{getRate(plan)}</Text>
            {isTieredBillingRate(plan.billingRate) && (
              <Text as="p" variant="text50" color="text.weak">
                {TIERED_CALCULATION_LABEL[plan.billingRate.calculationMethod]}
              </Text>
            )}
          </Box>
        </BillingPlanTableBodyRowCell>
        <BillingPlanTableBodyRowCell isLastIndex={isLastIndex}>
          <Box css={{ maxWidth: '17rem' }}>
            <Text aria-label="billingPlanFeeAssessmentSource">{PaymentTypeMap[plan.paymentType]}</Text>
          </Box>
        </BillingPlanTableBodyRowCell>
        <BillingPlanTableBodyRowCell isLastIndex={isLastIndex}>
          <Box css={{ maxWidth: '20rem' }}>
            <NumberOfClients numClients={plan.numberOfClients} />
          </Box>
          <Sheet.Content css={{ overflowY: 'auto', gap: 'space.300' }}>
            <VisuallyHidden asChild={true}>
              <Sheet.Title>{plan.name}</Sheet.Title>
            </VisuallyHidden>
            <VisuallyHidden asChild={true}>
              <Sheet.Description>{plan.latestChangeDescription}</Sheet.Description>
            </VisuallyHidden>
            <BillingPlanPageDrawer planName={plan.name} numberOfClients={plan.numberOfClients} planId={plan.id} />
          </Sheet.Content>
        </BillingPlanTableBodyRowCell>
        <BillingPlanTableBodyRowCell isLastIndex={isLastIndex}>
          <Box css={{ maxWidth: '24rem' }}>
            <PlatformFeeText plan={plan} platformFee={platformFee} />
          </Box>
        </BillingPlanTableBodyRowCell>
        <BillingPlanTableBodyRowCell isLastIndex={isLastIndex}>
          <Box css={{ display: 'flex', justifyContent: 'end', float: 'right', maxWidth: '5rem' }}>
            <DropdownMenu aria-label="billingPlanOptions">
              <DropdownMenu.Trigger asChild={true}>
                <IconButton name="Ellipses" label="dropdownButton" spacing="compact" />
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                <Link to={urlBuilder.getEditBillingPlanFlowPage({ billingPlanId: plan.id, id: nanoid() })}>
                  <DropdownMenu.Item aria-label="billingPlanEditButton">Edit</DropdownMenu.Item>
                </Link>
                {!plan.firmDefault && (
                  <DropdownMenu.Item
                    aria-label="billingPlanSetDefaultButton"
                    onSelect={() => setDefaultModalIsOpen(true)}
                  >
                    Set as default
                  </DropdownMenu.Item>
                )}
              </DropdownMenu.Content>
            </DropdownMenu>
            <SetDefaultModal
              isOpen={isDefaultModalIsOpen}
              closeHandler={() => setDefaultModalIsOpen(false)}
              billingPlan={plan}
              defaultBillingPlan={defaultBillingPlan}
            />
          </Box>
        </BillingPlanTableBodyRowCell>
      </Sheet>
    </Table.Row>
  );
};

export default BillingPlanIndexPageRow;
